import { Button, DatePicker, Form, Input, Select, Spin } from "antd";
import { useMemo, useState } from "react";
import { formatDate, getDatePickerValue } from "./KPI/kpiHelpers";
import { useDispatch, useSelector } from "react-redux";
import {
  getDataAnalysisExcel,
  getDataAnalysisICSIRegister,
} from "redux/reducers/Dashboard/dashboard.slice";

const dataAnalysisInitialData = {
  start_date: null,
  end_date: null,
  start_age: null,
  end_age: null,
  location_id: null,
};

export function DataAnalysis() {
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const { locationData } = useSelector(({ role }) => role);

  const { dataAnalysisExcelLoading } = useSelector(
    ({ dashboard }) => dashboard
  );

  const [dataAnalysisInput, setDataAnalysisInput] = useState(
    dataAnalysisInitialData
  );

  const onChangeDataAnalysisInput = (name, values) => {
    const value =
      name === "start_date" || name === "end_date"
        ? formatDate(values)
        : values;

    setDataAnalysisInput((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const locationOptions = useMemo(
    () =>
      locationData.map((item) => ({
        label: item.location_name,
        value: item.location_id,
      })) || [],
    [locationData]
  );

  const onFinish = () => {
    dispatch(
      getDataAnalysisExcel({
        start: null,
        limit: null,
        payload: dataAnalysisInput,
      })
    );
  };

  const onFinishFailed = (errorInfo) => {
    console.error("Failed:", errorInfo);
  };

  const onICSIRegisterClick = async () => {
    try {
      await form.validateFields(["start_date", "end_date"]);

      dispatch(
        getDataAnalysisICSIRegister({
          start: null,
          limit: null,
          payload: dataAnalysisInput,
        })
      );
    } catch (error) {
      console.log("Validation Failed:", error);
    }
  };

  return (
    <>
      {dataAnalysisExcelLoading && (
        <Spin tip="Loading" size="large">
          <div className="content" />
        </Spin>
      )}

      <Form
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        initialValues={dataAnalysisInitialData}
      >
        <div className="page_inner_content border-0 data_analysis_content">
          <div className="form_process_wrapper p-0  d-flex justify-content-between align-items-center flex-wrap">
            <ul className="grid_wrapper">
              <li className="w_220 w_xs_50">
                <Form.Item
                  label="Start Date"
                  name="start_date"
                  dependencies={["end_date"]}
                  rules={[
                    {
                      required: true,
                      message: "",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const endDate = getFieldValue("end_date");
                        if (!value || !endDate || value.isBefore(endDate)) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Start date must be before end date")
                        );
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    placeholder="Select Start Date"
                    format={{
                      format: "DD-MM-YYYY",
                      type: "mask",
                    }}
                    onChange={(e) => {
                      onChangeDataAnalysisInput("start_date", e);
                    }}
                    value={getDatePickerValue(dataAnalysisInput?.start_date)}
                  />
                </Form.Item>
              </li>
              <li className="w_220 w_xs_50">
                <Form.Item
                  label="End Date"
                  name="end_date"
                  dependencies={["start_date"]}
                  rules={[
                    {
                      required: true,
                      message: "",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const startDate = getFieldValue("start_date");
                        if (!value || !startDate || value.isAfter(startDate)) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("End date must be after start date")
                        );
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    placeholder="Select End Date"
                    format={{
                      format: "DD-MM-YYYY",
                      type: "mask",
                    }}
                    onChange={(e) => {
                      onChangeDataAnalysisInput("end_date", e);
                    }}
                    value={getDatePickerValue(dataAnalysisInput?.end_date)}
                  />
                </Form.Item>
              </li>
              <li className="w_220 w_xs_50">
                <Form.Item
                  label="Start Age"
                  name="start_age"
                  dependencies={["end_age"]}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const endAge = parseInt(getFieldValue("end_age"));
                        const fieldValue = parseInt(value);
                        if (!value || !endAge || fieldValue < endAge) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Start age must be less than end age")
                        );
                      },
                    }),
                  ]}
                >
                  <Input
                    placeholder="Start Age"
                    className="w-100"
                    onChange={(e) => {
                      onChangeDataAnalysisInput(
                        "start_age",
                        parseInt(e.target.value)
                      );
                    }}
                    value={dataAnalysisInput?.start_age}
                  />
                </Form.Item>
              </li>
              <li className="w_220 w_xs_50">
                <Form.Item
                  label="End Age"
                  name="end_age"
                  dependencies={["start_age"]}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const startAge = parseInt(getFieldValue("start_age"));
                        const fieldValue = parseInt(value);
                        if (!value || !startAge || fieldValue > startAge) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("End age must be greater than start age")
                        );
                      },
                    }),
                  ]}
                >
                  <Input
                    placeholder="End Age"
                    className="w-100"
                    onChange={(e) => {
                      onChangeDataAnalysisInput(
                        "end_age",
                        parseInt(e.target.value)
                      );
                    }}
                    value={dataAnalysisInput?.end_age}
                  />
                </Form.Item>
              </li>
              <li className="w_220 w_xs_50">
                <Form.Item
                  label="Location"
                  name="location_id"
                  className="custom_select mb-0"
                >
                  <Select
                    placeholder="Select Location"
                    name="location_id"
                    value={dataAnalysisInput?.location_id}
                    onChange={(value) => {
                      onChangeDataAnalysisInput("location_id", value);
                    }}
                    options={[...locationOptions]}
                  />
                </Form.Item>
              </li>
            </ul>

            <ul className="grid_wrapper">
              <Button className="btn_primary mx-sm-2 mx-1" htmlType="submit">
                Export Excel
              </Button>
              <Button
                className="btn_primary mx-sm-2 mx-1"
                onClick={onICSIRegisterClick}
              >
                ICSI Register
              </Button>
            </ul>
          </div>
        </div>
      </Form>
    </>
  );
}
